import React, { useState, useEffect } from 'react'
import { Box, Typography } from '@mui/material'
import GlobeComponent from './GlobeComponent'
import './MainSection.css'
import axios from 'axios'

interface TypewriterProps {
  text: string
  typingSpeed?: number // ms per character when typing
  deletingSpeed?: number // ms per character when deleting
  pauseTime?: number // ms to wait after full text is typed / deleted
}

const Typewriter: React.FC<TypewriterProps> = ({
  text,
  typingSpeed = 150,
  deletingSpeed = 100,
  pauseTime = 1000,
}) => {
  const [display, setDisplay] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [charIndex, setCharIndex] = useState(0)
  const [pause, setPause] = useState(false)

  useEffect(() => {
    if (pause) {
      const pauseTimer = setTimeout(() => setPause(false), pauseTime)
      return () => clearTimeout(pauseTimer)
    }

    const speed = isDeleting ? deletingSpeed : typingSpeed
    const timer = setTimeout(() => {
      if (!isDeleting) {
        // Typing
        const next = charIndex + 1
        setDisplay(text.slice(0, next))
        setCharIndex(next)
        if (next === text.length) {
          setPause(true)
          setIsDeleting(true)
        }
      } else {
        // Deleting
        const next = charIndex - 1
        setDisplay(text.slice(0, next))
        setCharIndex(next)
        if (next === 0) {
          setPause(true)
          setIsDeleting(false)
        }
      }
    }, speed)

    return () => clearTimeout(timer)
  }, [
    charIndex,
    isDeleting,
    pause,
    text,
    typingSpeed,
    deletingSpeed,
    pauseTime,
  ])

  return (
    <span style={{ fontSize: 'inherit', lineHeight: 'inherit' }}>
      {display}
    </span>
  )
}

const MainSection: React.FC = () => {
  const [data, setData] = useState<{ h1: string; h2: string; h3: string }>({
    h1: '',
    h2: '',
    h3: '',
  })

  useEffect(() => {
    axios
      .get('https://app.dfopsa.online/api/main-section')
      .then(response => {
        setData(response.data.data)
      })
      .catch(error => {
        console.error('Axios error:', error)
      })
      .finally(() => {})
  }, [])

  return (
    <Box
      className='mainSection'
      sx={{ width: '100%', mt: { xs: '80px', md: 0 } }}
    >
      <Box
        className='innerSectionGlobe'
        sx={{
          flexDirection: { xs: 'column-reverse', md: 'row' },
          margin: { xs: '40px 40px 0px', md: '96px 96px 30px' },
          alignItems: 'center',
        }}
      >
        <Box sx={{ width: { xs: '100%', md: '50%' } }}>
          <Typography
            sx={{ fontSize: { xs: 24, md: 40 }, lineHeight: 1.2 }}
            className='fadeInBox fontHero'
          >
            {data.h1.split(' ').map((word, index) => (
              <span
                key={index}
                className='fade-word'
                style={{ animationDelay: `${index * 0.3}s` }}
              >
                {word}&nbsp;
              </span>
            ))}
          </Typography>

          <Typography
            sx={{ fontSize: { xs: 16, md: 40 }, lineHeight: 1.2 }}
            className='ellipse2Box '
          >
            {data.h3.split(' ').map((word, index) => (
              <span
                key={index}
                className='ellipse2'
                style={{ animationDelay: `${index * 0.2}s` }} // slower: 0.3s delay per word
              >
                {word}&nbsp;
              </span>
            ))}
          </Typography>

          <Typography
            sx={{ height: '70px' }}
            variant='h3'
            component='span'
            className='gradientTextBox'
          >
            <Box
              component='span'
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
              }}
            >
              <Typewriter text={data.h2} />
            </Box>
          </Typography>
        </Box>
        <Box
          sx={{
            width: { xs: '100%', md: '50%' },
            height: { xs: '250px', sm: '400px' },
            position: 'relative',
          }}
        >
          <GlobeComponent />
        </Box>
      </Box>
    </Box>
  )
}

export default MainSection
